/**
 * BookingPage - Main public booking page for scheduling meetings.
 * Fetches meeting type by URL params and routes to appropriate step.
 */

import {
  endOfMonth,
  getLocalTimeZone,
  startOfMonth,
  today,
} from '@internationalized/date';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { BookingCalendar } from '@/components/scheduling/BookingCalendar';
import { BookingErrorBoundary } from '@/components/scheduling/BookingErrorBoundary';
import {
  BookingForm,
  type BookingFormData,
} from '@/components/scheduling/BookingForm';
import { BookingPageSkeleton } from '@/components/scheduling/BookingPageSkeleton';
import type { BookingSuccessData } from '@/components/scheduling/BookingSuccess';
import { BookingSuccess } from '@/components/scheduling/BookingSuccess';
import {
  bookMeeting,
  bookMeetingByCode,
  bookMeetingByGlobalSlug,
  getAvailability,
  getAvailabilityByCode,
  getPublicBookingType,
  getPublicBookingTypeByCode,
  getPublicBookingTypeByGlobalSlug,
} from '@/lib/api/scheduling';
import type {
  AvailabilityResponse,
  AvailableSlot,
  CustomQuestion,
  PublicBookingType,
} from '@/lib/api/types/scheduling';
import { logger } from '@/lib/logger';
import {
  isEmbedActive,
  postBookingSuccess,
  preserveBookingParams,
} from '@/lib/scheduling/bookingEmbed';
import { BookingHostCard } from '@/lib/scheduling/components/BookingHostCard';
import { isMultiHostSchedulingType } from '@/lib/scheduling/hostIdentity';
import {
  detectBrowserTimezone,
  useTimezone,
} from '@/lib/scheduling/useTimezone';
import { cx } from '@/utils/cx';

import { BookingLayout } from './BookingLayout';

/**
 * Format a CalendarDate as the `YYYY-MM-DD` shape the availability endpoint
 * expects. Kept inline because BookingCalendar uses the same pattern and the
 * helper is too small to justify a shared utility.
 */
function calendarDateToIso(d: { year: number; month: number; day: number }) {
  return `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`;
}

type BookingStep = 'calendar' | 'form' | 'success';

// Stable empty fallback so the no-questions path keeps a constant prop
// identity across renders instead of a fresh `?? []` array each time.
const EMPTY_CUSTOM_QUESTIONS: CustomQuestion[] = [];

interface BookingErrorProps {
  title: string;
  message: string;
}

function BookingError({ title, message }: BookingErrorProps) {
  return (
    <div className='text-center py-12'>
      <div className='inline-flex items-center justify-center w-16 h-16 rounded-full bg-[var(--color-error-100)] mb-4'>
        <svg
          className='w-8 h-8 text-[var(--color-text-error-primary)]'
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
          />
        </svg>
      </div>
      <h1 className='text-xl font-semibold text-fg-primary mb-2'>{title}</h1>
      <p className='text-fg-secondary max-w-md mx-auto'>{message}</p>
    </div>
  );
}

export function BookingPage() {
  const { username, slug, shortCode, globalSlug } = useParams<{
    username?: string;
    slug?: string;
    shortCode?: string;
    globalSlug?: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const embedActive = isEmbedActive(searchParams);

  const [meetingType, setMeetingType] = useState<PublicBookingType | null>(
    null
  );
  // Initial availability is fetched alongside the meeting type so the
  // skeleton can stay up until the calendar grid is ready to render. When
  // populated, we hand this to <BookingCalendar /> and it skips its first
  // fetch — no spinner appears between the skeleton and the populated
  // calendar. See the cross-component contract on `initialAvailability` in
  // BookingCalendar's prop docs.
  const [initialAvailability, setInitialAvailability] =
    useState<AvailabilityResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<BookingStep>('calendar');
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);
  const [booking, setBooking] = useState<BookingSuccessData | null>(null);
  const [, setIsSubmitting] = useState(false);

  // Selected viewing timezone, persisted in sessionStorage (tab-scoped). Hooks MUST be
  // declared before any early return so the first render and subsequent
  // renders agree on hook count (rules of hooks).
  const {
    timezone: selectedTimezone,
    setTimezone: setSelectedTimezone,
    availableTimezones,
  } = useTimezone();

  // Reset handler for error boundary - navigates back to calendar step
  const handleErrorReset = useCallback(() => {
    setSearchParams(prev => preserveBookingParams(prev));
    setStep('calendar');
  }, [setSearchParams]);

  // Handle slot selection from calendar
  const handleSlotSelect = useCallback(
    (slot: AvailableSlot) => {
      setSelectedSlot(slot);
      setSearchParams(prev => preserveBookingParams(prev, 'form'));
      setStep('form');
    },
    [setSearchParams]
  );

  // Handle back from form to calendar
  const handleBackToCalendar = useCallback(() => {
    setSearchParams(prev => preserveBookingParams(prev));
    setStep('calendar');
  }, [setSearchParams]);

  // Handle form submission
  const handleFormSubmit = useCallback(
    async (formData: BookingFormData) => {
      if (!selectedSlot || !meetingType) {
        return;
      }

      setIsSubmitting(true);
      try {
        const bookingRequest = {
          start_time: selectedSlot.start,
          end_time: selectedSlot.end,
          booker_email: formData.email,
          booker_name: formData.name,
          // The booker's selected viewing timezone — stored on the booking
          // (ahoy_timezone) so confirmation emails and host-side views render
          // times in the timezone the booker actually chose, not UTC.
          timezone: selectedTimezone,
          notes: formData.notes || undefined,
          additional_guests:
            formData.additionalGuests.length > 0
              ? formData.additionalGuests
              : undefined,
          ...(formData.customAnswers
            ? { custom_answers: formData.customAnswers }
            : {}),
        };

        let result;
        if (globalSlug) {
          result = await bookMeetingByGlobalSlug(globalSlug, bookingRequest);
        } else {
          const effectiveShortCode = shortCode || meetingType.short_code;
          if (effectiveShortCode) {
            result = await bookMeetingByCode(
              effectiveShortCode,
              bookingRequest
            );
          } else if (username && slug) {
            result = await bookMeeting(username, slug, bookingRequest);
          } else {
            throw new Error('Invalid booking configuration');
          }
        }

        // Transform the API response + form data into BookingSuccessData.
        // Reuse the selected viewing timezone (not the browser's) so the
        // confirmation displays the same times the booker just looked at.
        const bookingData: BookingSuccessData = {
          entity_id: result.entity_id,
          booker_name: formData.name,
          booker_email: formData.email,
          start_time: selectedSlot.start,
          end_time: selectedSlot.end,
          timezone: selectedTimezone,
          meeting_link: result.meeting_link,
          cancel_token: result.cancel_token ?? undefined,
          notes: formData.notes || undefined,
          additional_guests:
            formData.additionalGuests.length > 0
              ? formData.additionalGuests
              : undefined,
        };

        setBooking(bookingData);
        if (embedActive) {
          postBookingSuccess({
            bookingId: result.entity_id,
            meetingLink: result.meeting_link ?? '',
          });
        }
        setSearchParams(prev => preserveBookingParams(prev, 'success'));
        setStep('success');
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      selectedSlot,
      meetingType,
      shortCode,
      username,
      slug,
      globalSlug,
      selectedTimezone,
      embedActive,
      setSearchParams,
    ]
  );

  // Determine the step from search params (for back navigation)
  useEffect(() => {
    const stepParam = searchParams.get('step');
    if (stepParam === 'form') {
      // If navigating to form step but no slot is selected, redirect to calendar
      if (!selectedSlot) {
        setSearchParams(prev => preserveBookingParams(prev));
        setStep('calendar');
      } else {
        setStep('form');
      }
    } else if (stepParam === 'success') {
      setStep('success');
    } else {
      setStep('calendar');
    }
  }, [searchParams, selectedSlot, setSearchParams]);

  // Fetch meeting type AND initial availability based on URL params.
  //
  // We deliberately hold `isLoading=true` (and therefore keep the skeleton
  // up) until BOTH have resolved. The previous flow was:
  //
  //     skeleton → meeting type loads → page renders → BookingCalendar
  //     mounts → BookingCalendar shows its own centred spinner → spinner
  //     clears → calendar grid renders.
  //
  // That spinner caused a visible "skeleton → spinner → app" sequence and a
  // small layout reflow as the calendar region swapped from a centred
  // <LoadingIndicator /> back to the populated grid. By pre-fetching the
  // initial month's slots here and passing them to <BookingCalendar /> as
  // `initialAvailability`, the calendar mounts with `availability` already
  // populated and skips its first fetch — so the booker sees the skeleton
  // followed immediately by the fully populated calendar.
  //
  // We do NOT re-run this effect when `selectedTimezone` changes; the
  // calendar handles subsequent TZ-change refetches itself with the
  // dim-and-disable affordance, not a centred spinner.
  useEffect(() => {
    let cancelled = false;

    const initialTimezone =
      selectedTimezone || detectBrowserTimezone() || getLocalTimeZone();

    const fetchInitialAvailability = async (
      mt: PublicBookingType
    ): Promise<AvailabilityResponse | null> => {
      // Mirror BookingCalendar's first-mount range: startOfMonth(today) →
      // endOfMonth(today) + 1 month. Matching the range avoids a wasted
      // refetch when BookingCalendar's auto-select-earliest-date effect
      // doesn't need to jump months.
      const todayD = today(initialTimezone);
      const startDate = startOfMonth(todayD);
      const endDate = endOfMonth(todayD).add({ months: 1 });
      const opts = {
        start_date: calendarDateToIso(startDate),
        end_date: calendarDateToIso(endDate),
        timezone: initialTimezone,
      };
      try {
        // Every PublicBookingType response carries a short_code (the
        // serializer guarantees one even for legacy rows), so we can use
        // the short-code endpoint uniformly across /m/, /<u>/<s>/, and
        // /g/<global_slug>/ URLs.
        if (mt.short_code) {
          return await getAvailabilityByCode(mt.short_code, opts);
        }
        if (username && slug) {
          return await getAvailability(username, slug, opts);
        }
        return null;
      } catch (err) {
        // Don't fail the page on an availability pre-fetch error — let
        // BookingCalendar handle the failure with its own retry UI. We
        // still show the skeleton transition to a populated page, just
        // with the calendar in its error state instead of a grid.
        logger.warn('Initial availability prefetch failed', {
          component: 'BookingPage',
          error: String(err),
        });
        return null;
      }
    };

    const fetchAll = async () => {
      setIsLoading(true);
      setError(null);

      try {
        let result: PublicBookingType;

        if (globalSlug) {
          // Global slug URL: /:globalSlug
          result = await getPublicBookingTypeByGlobalSlug(globalSlug);
        } else if (shortCode) {
          // Short code URL: /m/:shortCode
          result = await getPublicBookingTypeByCode(shortCode);
        } else if (username && slug) {
          // User URL: /:username/:slug
          result = await getPublicBookingType(username, slug);
        } else {
          throw new Error('Invalid booking URL');
        }

        if (cancelled) {
          return;
        }

        const av = await fetchInitialAvailability(result);

        if (!cancelled) {
          setMeetingType(result);
          setInitialAvailability(av);
        }
      } catch (err) {
        if (!cancelled) {
          if (err instanceof Error) {
            if (err.message.includes('404')) {
              setError('This meeting type could not be found.');
            } else if (err.message.includes('400')) {
              setError('This meeting type is not currently available.');
            } else {
              setError('Unable to load meeting details. Please try again.');
            }
          } else {
            setError('An unexpected error occurred.');
          }
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchAll();
    return () => {
      cancelled = true;
    };
    // `selectedTimezone` is read at fetch time but does NOT re-trigger the
    // fetch — subsequent TZ changes are handled inside BookingCalendar's
    // own fetch effect, which uses the dim-and-disable affordance instead
    // of swapping in a centred spinner.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, slug, shortCode, globalSlug]);

  // Render content based on current step
  const renderStepContent = () => {
    if (!meetingType) {
      return null;
    }

    // For individual bookings the booker's auto-detected timezone is used
    // and the selector is hidden. Multi-host types (round robin /
    // collective) expose the picker because availability spans multiple
    // calendars and the booker may want to view in a different zone.
    const isMultiHost = isMultiHostSchedulingType(meetingType.scheduling_type);

    switch (step) {
      case 'calendar': {
        const effectiveShortCode = shortCode || meetingType.short_code;
        return (
          // `lg:min-h-[420px]` anchors the card height so the calendar →
          // form transition doesn't trigger a vertical recentre. The form
          // step's natural content height (~400px: 5 fields + button row
          // + space-y-5 gaps) is taller than the calendar step's ~360px;
          // without a shared floor the card would grow 40px and, because
          // BookingLayout's <main> uses `items-center justify-center`, the
          // top of the card would move up ~20px in the same frame the form
          // mounts. The booker perceives that re-centering as a "flash".
          // Pinning both step grids (and the skeleton) at 420px keeps the
          // card top stable across the swap.
          //
          // Embed modal is the exception: it hugs its content (the iframe
          // resizes to fit — it is NOT vertically centred), so the floor
          // would only reserve dead space and make the popup look oversized
          // on a sparse day. Drop it there and let the modal be
          // calendar-height; the calendar→form swap resizes the iframe
          // smoothly instead of re-centring, so there's no flash to prevent.
          <div
            className={cx(
              'grid grid-cols-1 gap-6 md:grid-cols-[260px_minmax(0,1fr)] lg:gap-10',
              !embedActive && 'lg:min-h-[420px]'
            )}
          >
            <BookingHostCard
              meetingType={meetingType}
              timezone={selectedTimezone}
              onTimezoneChange={isMultiHost ? setSelectedTimezone : undefined}
              availableTimezones={isMultiHost ? availableTimezones : undefined}
              className='md:border-r md:border-[var(--color-border-secondary)] md:pr-6 lg:pr-10'
            />
            <BookingCalendar
              meetingTypeId={effectiveShortCode || `${username}/${slug}`}
              isShortCode={!!effectiveShortCode}
              username={username}
              slug={slug}
              duration={meetingType.duration_minutes}
              timezone={selectedTimezone}
              onSlotSelect={handleSlotSelect}
              initialAvailability={initialAvailability ?? undefined}
              embed={embedActive}
            />
          </div>
        );
      }

      case 'form': {
        // If no slot selected, useEffect will redirect to calendar
        if (!selectedSlot) {
          return null;
        }
        // Reuse the same three-column layout from the calendar step so the
        // chrome stays consistent and the booker keeps event context as they
        // fill out the form. The host card grows two extra metadata rows
        // (date + time range) so the selected slot is visible at all times.
        const slotDate = new Date(selectedSlot.start);
        const slotEnd = new Date(selectedSlot.end);
        const dateLabel = new Intl.DateTimeFormat(undefined, {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric',
          timeZone: selectedTimezone,
        }).format(slotDate);
        const timeFormatter = new Intl.DateTimeFormat(undefined, {
          hour: 'numeric',
          minute: '2-digit',
          timeZone: selectedTimezone,
        });
        const timeLabel = `${timeFormatter.format(slotDate)} – ${timeFormatter.format(slotEnd)}`;
        return (
          // Same `lg:min-h-[420px]` floor as the calendar step (and the same
          // embed exception) — see the comment in the calendar case.
          <div
            className={cx(
              'grid grid-cols-1 gap-6 md:grid-cols-[260px_minmax(0,1fr)] lg:gap-10',
              !embedActive && 'lg:min-h-[420px]'
            )}
          >
            <BookingHostCard
              meetingType={meetingType}
              timezone={selectedTimezone}
              onTimezoneChange={isMultiHost ? setSelectedTimezone : undefined}
              availableTimezones={isMultiHost ? availableTimezones : undefined}
              selectedSlot={{ dateLabel, timeLabel }}
              className='md:border-r md:border-[var(--color-border-secondary)] md:pr-6 lg:pr-10'
            />
            <BookingForm
              selectedSlot={selectedSlot}
              bookingType={meetingType}
              timezone={selectedTimezone}
              customQuestions={
                meetingType.custom_questions ?? EMPTY_CUSTOM_QUESTIONS
              }
              onSubmit={handleFormSubmit}
              onBack={handleBackToCalendar}
              initialEmail={searchParams.get('email') ?? ''}
              initialName={searchParams.get('name') ?? ''}
            />
          </div>
        );
      }

      case 'success':
        if (!booking) {
          return (
            <div className='text-center py-12'>
              <h1 className='text-xl font-semibold text-fg-primary mb-2'>
                Booking confirmed
              </h1>
              <p className='text-fg-secondary'>
                You&apos;ll receive a confirmation email shortly.
              </p>
            </div>
          );
        }
        return <BookingSuccess booking={booking} meetingType={meetingType} />;

      default:
        return null;
    }
  };

  let content: ReactNode;
  if (isLoading) {
    content = <BookingPageSkeleton />;
  } else if (error || !meetingType) {
    content = (
      <BookingError
        title='Meeting not found'
        message={error || 'This meeting type could not be found.'}
      />
    );
  } else {
    content = (
      <BookingErrorBoundary onReset={handleErrorReset}>
        {renderStepContent()}
      </BookingErrorBoundary>
    );
  }

  return <BookingLayout embed={embedActive}>{content}</BookingLayout>;
}
